import { createFileRoute, Navigate } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import apiClient from '@/api/client'
import type { User } from '@/types'
import { defaultAdminPath, isStaffRole } from '@/lib/staff-roles'
import { useEdition } from '@/hooks/useEdition'

export const Route = createFileRoute('/')({
  component: HomePage,
})

function HomePage() {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const storedUser = localStorage.getItem('pos_user')
    const token = localStorage.getItem('pos_token')

    if (storedUser && token) {
      try {
        setUser(JSON.parse(storedUser))
      } catch {
        localStorage.removeItem('pos_user')
        localStorage.removeItem('pos_token')
      }
    }
    setIsLoading(false)
  }, [])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading POS System…</p>
        </div>
      </div>
    )
  }

  if (!apiClient.isAuthenticated() || !user) {
    return <Navigate to="/login" />
  }

  if (!isStaffRole(user.role)) {
    return <Navigate to="/login" />
  }

  return <AuthedLanding user={user} />
}

// Separate component so useEdition's settings fetch only fires once the user
// is known to be authenticated (it 401s otherwise). Waiting for the edition
// lands marketing-edition users in one hop instead of bouncing through
// /admin/dashboard; on fetch error fall back to the full-POS path and let
// the guard in routes/admin.tsx sort it out.
function AuthedLanding({ user }: { user: User }) {
  const { posEnabled, isLoading, isError } = useEdition()
  if (isLoading && !isError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading POS System…</p>
        </div>
      </div>
    )
  }
  return (
    <Navigate to={defaultAdminPath(user.role, isError ? true : posEnabled)} replace />
  )
}
